import { useState, useEffect } from "react";
import { Code2, Cpu, Zap, CheckCircle2, ArrowRight, Sparkles, Network, Workflow, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "react-router-dom";
import Footer from "@/components/Footer";
import BlogPostImage from "@/components/BlogPostImage";
import LoadingScreenAlt from "@/components/LoadingScreenAlt";

const Index = () => {
  const [showLoading, setShowLoading] = useState(true);

  useEffect(() => {
    // Mostrar loading apenas na primeira visita da sessão
    const hasVisited = sessionStorage.getItem("hasVisited");
    if (hasVisited) {
      setShowLoading(false);
    } else {
      sessionStorage.setItem("hasVisited", "true");
    }
  }, []);

  const handleLoadingComplete = () => {
    setShowLoading(false);
  };
  const services = [
    {
      icon: Code2,
      title: "Desenvolvimento de Software",
      description: "Soluções personalizadas com tecnologias modernas para transformar suas ideias em realidade digital.",
      features: [
        "Aplicações Web e Mobile",
        "Sistemas sob medida",
        "Integração de APIs",
        "Arquitetura escalável"
      ],
      link: "/desenvolvimento-software"
    },
    {
      icon: Cpu,
      title: "Automação de Processos",
      description: "Automatize tarefas repetitivas e aumente a eficiência operacional do seu negócio.",
      features: [
        "RPA (Robotic Process Automation)",
        "Integração de sistemas",
        "Workflows inteligentes",
        "Redução de custos"
      ],
      link: "/automacao-processos"
    },
    {
      icon: Shield,
      title: "Segurança Digital",
      description: "Proteção completa com soluções Acronis: backup em nuvem, antivírus, disaster recovery e muito mais.",
      features: [
        "Cyber Backup Cloud",
        "Advanced Security",
        "Disaster Recovery",
        "Email Security"
      ],
      link: "/seguranca-digital"
    },
    {
      icon: Network,
      title: "Consultoria em TI",
      description: "Orientação estratégica para otimizar seus processos e implementar as melhores soluções tecnológicas.",
      features: [
        "Análise de processos",
        "Estratégia digital",
        "Otimização de sistemas",
        "Suporte técnico especializado"
      ],
      link: "/consultoria-ti"
    }
  ];

  return (
    <>
      {showLoading && <LoadingScreenAlt onLoadingComplete={handleLoadingComplete} duration={2500} />}
      
      <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Vertical lines pattern overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--border)/0.3)_1px,transparent_1px)] bg-[size:8rem_100%] pointer-events-none z-0" style={{ maskImage: 'radial-gradient(ellipse 80% 50% at 50% 0%, black 70%, transparent 110%), linear-gradient(to bottom, black 0%, black 85%, transparent 100%)', maskComposite: 'intersect', WebkitMaskImage: 'radial-gradient(ellipse 80% 50% at 50% 0%, black 70%, transparent 110%), linear-gradient(to bottom, black 0%, black 85%, transparent 100%)', WebkitMaskComposite: 'source-in' }} />

      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-32 left-4 md:left-10 w-64 h-64 bg-primary/40 rounded-full blur-3xl animate-float z-20" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent/20 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-3xl animate-glow" />
      </div>

      {/* Header */}
      <header className="border-b border-border/50 bg-card/30 backdrop-blur-xl sticky top-0 z-50 relative">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/">
            <img src="/logo.png" alt="Coddar Logo" className="h-11 w-auto" />
          </Link>
          <nav className="hidden md:flex gap-6">
            <a href="#servicos" className="text-muted-foreground hover:text-primary transition-colors">
              Serviços
            </a>
            <Link to="/blog" className="text-muted-foreground hover:text-primary transition-colors">
              Blog
            </Link>
            <Link to="/seguranca-digital" className="text-muted-foreground hover:text-primary transition-colors">
              Segurança Digital
            </Link>
            <Link to="/contato" className="text-muted-foreground hover:text-primary transition-colors">
              Contato
            </Link>
          </nav>
          <Button asChild variant="outline" className="border-primary/50 text-primary hover:bg-primary/10 hover:border-primary">
            <a href="https://folio.app.br/area-cliente">Área do Cliente</a>
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 md:py-28 px-4 relative">
        <div className="container mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full mb-6 backdrop-blur-sm animate-fade-in">
            <Sparkles className="h-4 w-4 text-primary animate-pulse" />
            <span className="text-sm font-medium text-primary">Desenvolvimento & Automação</span>
          </div>
          <h1 className="text-5xl md:text-8xl text-foreground mb-6 max-w-6xl mx-auto leading-tight animate-fade-in relative z-0" style={{ animationDelay: '0.1s' }}>
            <span className="font-light">TRANSFORME SEU</span>{" "}
            <span className="font-bold">NEGÓCIO</span>{" "}
            <span className="font-light">COM</span>{" "}
            <span className="font-bold text-primary">TECNOLOGIA</span>{" "}
            <span className="font-light">DE PONTA</span>
          </h1>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto animate-fade-in" style={{ animationDelay: '0.2s' }}>
            Desenvolvemos software sob medida e automatizamos processos para elevar a eficiência da sua empresa ao próximo nível
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in" style={{ animationDelay: '0.3s' }}>
            <Button size="lg" asChild className="gap-2 group relative overflow-hidden">
              <Link to="/contato" className="relative z-10 flex items-center gap-2">
                Solicitar Orçamento <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild className="border-primary/20 hover:border-primary/50 hover:bg-primary/10">
              <a href="#servicos">Ver Projetos</a>
            </Button>
          </div>

          {/* Floating tech icons */}
          <div className="absolute inset-0 pointer-events-none">
            <Workflow className="absolute top-20 left-[15%] h-12 w-12 text-primary/30 animate-float" />
            <Zap className="absolute bottom-32 right-[20%] h-10 w-10 text-accent/30 animate-float" style={{ animationDelay: '1s' }} />
            <Cpu className="absolute top-40 right-[15%] h-14 w-14 text-primary/20 animate-float" style={{ animationDelay: '3s' }} />
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="servicos" className="py-24 md:py-32 px-4 relative">
        <div className="container mx-auto relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full mb-4 backdrop-blur-sm">
              <Sparkles className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-primary">Nossas Especialidades</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-4">
              Soluções Tecnológicas Completas
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Do desenvolvimento ao deploy, da automação à consultoria
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => {
              const Icon = service.icon;

              return (
                <Link key={index} to={service.link} className="block">
                  <Card
                    className="border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/50 transition-all duration-300 hover:shadow-[0_0_30px_rgba(99,159,255,0.2)] hover:scale-[1.02] group animate-fade-in h-full"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <CardHeader>
                      <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform relative">
                        <Icon className="h-7 w-7 text-primary relative z-10" />
                        <div className="absolute inset-0 bg-primary/30 blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      <CardTitle className="text-xl text-foreground">{service.title}</CardTitle>
                      <CardDescription className="text-base text-muted-foreground">
                        {service.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2 mb-4">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2 group/item">
                            <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5 group-hover/item:scale-110 transition-transform" />
                            <span className="text-sm text-muted-foreground">{feature}</span>
                          </li>
                        ))}
                      </ul>
                      {service.link && (
                        <Button variant="ghost" className="w-full group/btn">
                          <span className="flex items-center gap-2">
                            Saiba mais <ArrowRight className="h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                          </span>
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Tech Stack Section */}
      <section className="py-20 md:py-28 px-4 relative">
        <div className="container mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left side - Text */}
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full mb-6 backdrop-blur-sm">
                <Sparkles className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium text-primary">Nosso Stack</span>
              </div>
              <h2 className="text-3xl md:text-5xl font-light text-foreground mb-6">
                Explore Nossas{" "}
                <span className="text-primary font-normal">Tecnologias &</span>{" "}
                <span className="text-primary font-normal">Experiência</span>
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Utilizamos as tecnologias mais modernas e robustas do mercado para entregar soluções de alta qualidade e performance.
              </p>
              <Button size="lg" asChild className="gap-2 group">
                <Link to="/desenvolvimento-software">
                  Saiba Mais <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
            </div>

            {/* Right side - Tech Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { name: "PHP", level: "95%", icon: "/php-icon.svg" },
                { name: "Laravel", level: "93%", icon: "/laravel-icon.svg" },
                { name: "WordPress", level: "90%", icon: "/wordpress-icon.svg" },
                { name: "Python", level: "88%", icon: "/python-icon.svg" },
                { name: "React", level: "92%", icon: "/reactjs-icon.svg" },
                { name: "TypeScript", level: "90%", icon: "/typescript-icon.svg" },
                { name: "Node.js", level: "87%", icon: "/nodejs-icon.svg" },
                { name: "MySQL", level: "94%", icon: "/mysql-icon.svg" }
              ].map((tech, index) => (
                <Card
                  key={index}
                  className="border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/50 transition-all duration-300 hover:scale-105 group animate-fade-in"
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <CardContent className="p-6 text-center">
                    <div className="mb-3 group-hover:scale-110 transition-transform flex items-center justify-center">
                      <img src={tech.icon} alt={tech.name} className="w-12 h-12 object-contain" />
                    </div>
                    <h3 className="font-semibold text-foreground mb-2">{tech.name}</h3>
                    <p className="text-sm text-muted-foreground">{tech.level}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Website Development & AEO Section */}
      <section className="py-20 md:py-28 px-4 relative">
        <div className="container mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left side - Text */}
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full mb-6 backdrop-blur-sm">
                <Zap className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium text-primary">Desenvolvimento Web</span>
              </div>
              <h2 className="text-3xl md:text-5xl font-light text-foreground mb-6">
                Sites de{" "}
                <span className="text-primary font-normal">Alta Performance</span>{" "}
                Otimizados para{" "}
                <span className="text-primary font-normal">IA</span>
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Transforme seu site em um ativo digital que vende 24/7 e é reconhecido como autoridade
                por buscadores e inteligências artificiais.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Performance 95+</h3>
                    <p className="text-sm text-muted-foreground">Arquitetura moderna que elimina a lentidão</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">AEO Otimizado</h3>
                    <p className="text-sm text-muted-foreground">Seu site como fonte de respostas para IAs</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Conversão Máxima</h3>
                    <p className="text-sm text-muted-foreground">Design focado em transformar visitas em vendas</p>
                  </div>
                </div>
              </div>
              <Button asChild size="sm" className="w-fit mx-auto gap-2 group px-3 py-2" style={{ width: '120px' }}>
                <Link to="/diagnostico-gratuito" className="flex items-center justify-center gap-2 text-base">
                  Diagnóstico Gratuito <Zap className="h-4 w-4 group-hover:scale-110 transition-transform" />
                </Link>
              </Button>
            </div>

            {/* Right side - CTA Card */}
            <div>
              <Card className="border-border/50 bg-card/50 backdrop-blur-sm p-8">
                <div className="space-y-4">
                  <div className="text-center">
                    <h3 className="text-2xl font-bold text-foreground mb-2">
                      Pronto para transformar seu site?
                    </h3>
                    <p className="text-muted-foreground mb-6">
                      Descubra como podemos aumentar a performance e as conversões do seu site.
                    </p>
                  </div>
                  <Button
                    asChild
                    size="sm"
                    className="!w-fit !max-w-xs mx-auto block gap-2 group px-3 py-2"
                  >
                    <Link to="/diagnostico-gratuito" className="flex items-center gap-2 text-base">
                      Diagnóstico Gratuito <Zap className="h-4 w-4 group-hover:scale-110 transition-transform" />
                    </Link>
                  </Button>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Section */}
      <section className="py-20 md:py-28 px-4 relative">
        <div className="container mx-auto relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full mb-4 backdrop-blur-sm">
              <Sparkles className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-primary">NEW & BLOG</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-4">
              Últimas <span className="text-primary">Notícias</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Fique por dentro das novidades e tendências em tecnologia
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {[
              {
                title: "Tendências em Desenvolvimento Web para 2026",
                image: "/assets/blog-tendencias-web-2026.webp",
                excerpt: "Descubra as principais tecnologias e frameworks que estão moldando o futuro do desenvolvimento web.",
                link: "/blog/tendencias-desenvolvimento-web-2026",
                icon: <Code2 className="w-full h-full" />
              },
              {
                title: "Como a Automação Pode Transformar Seu Negócio",
                image: "/assets/blog-automacao-negocio.webp",
                excerpt: "Entenda como processos automatizados podem aumentar a eficiência e reduzir custos operacionais.",
                link: "/blog/automacao-transformar-negocio",
                icon: <Cpu className="w-full h-full" />
              },
              {
                title: "Segurança Digital: Proteja Seus Dados",
                image: "/assets/blog-seguranca-digital.webp",
                excerpt: "Conheça as melhores práticas para manter seus dados e sistemas seguros contra ameaças digitais.",
                link: "/blog/seguranca-digital-proteja-dados",
                icon: <Shield className="w-full h-full" />
              }
            ].map((post, index) => (
              <Link key={index} to={post.link} className="block">
                <Card
                  className="border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/50 transition-all duration-300 hover:scale-[1.02] group overflow-hidden h-full"
                >
                  <div className="aspect-video overflow-hidden">
                    <BlogPostImage
                      title={post.title}
                      backgroundImage={post.image}
                      icon={post.icon}
                    />
                  </div>
                  <CardHeader>
                    <CardTitle className="text-xl group-hover:text-primary transition-colors">
                      {post.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">{post.excerpt}</p>
                    <span className="inline-flex items-center gap-2 text-primary group-hover:gap-3 transition-all">
                      Leia mais <ArrowRight className="h-4 w-4" />
                    </span>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>

          {/* Ver Todos Button */}
          <div className="text-center">
            <Button asChild variant="outline" size="lg" className="gap-2 group border-primary/30 hover:border-primary hover:bg-primary hover:text-primary-foreground">
              <Link to="/blog">
                Ver Todas as Notícias <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 px-4 relative">
        <div className="container mx-auto relative z-10">
          <Card className="bg-gradient-to-br from-primary/20 via-card/50 to-accent/20 border-primary/30 backdrop-blur-xl overflow-hidden relative group">
            <div className="absolute inset-0 bg-gradient-to-r from-primary/0 via-primary/10 to-primary/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
            <div className="flex flex-col items-center justify-center py-12 md:py-12 relative z-10">
              <CardTitle className="text-3xl md:text-5xl mb-4 text-foreground text-center">
                Pronto para inovar?
              </CardTitle>
              <CardDescription className="text-foreground/80 text-lg max-w-2xl mx-auto text-center mb-6 px-4">
                Entre em contato e descubra como podemos transformar seus processos com tecnologia de ponta
              </CardDescription>
              <Button size="lg" asChild className="gap-2 group/btn bg-primary text-primary-foreground hover:bg-primary/90 relative overflow-hidden">
                <Link to="/contato" className="relative z-10 flex items-center gap-2">
                  Agendar Consulta Gratuita <ArrowRight className="h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                </Link>
              </Button>
            </div>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
    </>
  );
};

export default Index;
